import PropTypes from "prop-types";
import React, { PureComponent } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";
import { connect } from "react-redux";

import FormButton from "../../components/form/FormButton";
import FormDropdown from "../../components/form/FormDropdown";
import ModalDescription from "../../components/modal/ModalDescription";
import ModalField from "../../components/modal/ModalField";
import ModalGroup from "../../components/modal/ModalGroup";
import ModalRow from "../../components/modal/ModalRow";
import ModalTabContent from "../../components/modal/ModalTabContent";
import { settingsErrorMessages } from "../../constants/settingsErrors";
import { typeOperations } from "../../modules/definition/types";
import preprocessorQuery from "../../utils/preprocessorQuery";
import selectors from "./selectors";
import { checkboxForDefinitionObject, wrapCheckboxGroup, wrapComponents } from "./utils";

const wrapDefaultField = (field, key = null) => {
  let component = field;
  //#!if iOS === true
// component = <ModalRow key={key}>{field}</ModalRow>;
  //#!endif
  return component;
};

const wrapDefaultFields = (fields, key = null) => {
  let component = null;
  //#!if iOS === true
// component = (
// <ModalGroup key={key} label="Defaults">
// {fields}
// </ModalGroup>
// );
  //#!else
  component = <ModalRow key={key}>{fields}</ModalRow>;
  //#!endif
  return component;
};

const renderDefaultDropdowns = useProps => {
  const onChangeDefaultSequenceType = id => useProps.dispatchLocal(typeOperations.changeDefaultSequenceType(id));
  const onChangeDefaultNarrativeType = id => useProps.dispatchLocal(typeOperations.changeDefaultNarrativeType(id));

  const sequenceTypes = useProps.orderedSequenceTypes
    .map(t => {
      return { value: t.id, label: t.label, icon: t.icon, color: t.color };
    })
    .toArray();

  const narrativeTypes = useProps.orderedNarrativeTypes
    .map(t => {
      return { value: t.id, label: t.label, icon: t.icon, color: t.color };
    })
    .toArray();

  const sequenceTypeField = wrapDefaultField(
    <ModalField key="sequenceTypeField" label="Default Chronological Type">
      <FormDropdown
        options={sequenceTypes}
        value={useProps.defaultSequenceType}
        onChange={state => onChangeDefaultSequenceType(state)}
      />
    </ModalField>,
    "sequenceTypeField"
  );

  const narrativeTypeField = useProps.includeNarrative
    ? wrapDefaultField(
      <ModalField key="narrativeTypeField" label="Default Narrative Type">
        <FormDropdown
          options={narrativeTypes}
          value={useProps.defaultNarrativeType}
          onChange={state => onChangeDefaultNarrativeType(state)}
        />
      </ModalField>,
      "narrativeTypeField"
    )
    : null;

  return wrapDefaultFields([sequenceTypeField, narrativeTypeField], "defaultTypeFields");
};

const renderTypes = useProps => {
  const isIos = preprocessorQuery.isIos();
  const eventTypes = selectors.eventTypesFromAllTypesExcludingNarrativeFolder(useProps.types);
  const personTypes = selectors.personTypesFromAllTypesExcludingNarrativeFolder(useProps.types);
  const locationTypes = selectors.locationTypesFromAllTypesExcludingNarrativeFolder(useProps.types);
  const otherTypes = selectors.otherTypesFromAllTypesExcludingNarrativeFolder(useProps.types);

  const onChange = (id, enabled) => useProps.dispatchLocal(typeOperations.changeEnabled(id, enabled));

  const eventCheckboxes = eventTypes.map(t => checkboxForDefinitionObject(t, onChange)).toArray();
  const personCheckboxes = personTypes.map(t => checkboxForDefinitionObject(t, onChange)).toArray();
  const locationCheckboxes = locationTypes.map(t => checkboxForDefinitionObject(t, onChange)).toArray();
  const otherCheckboxes = otherTypes.map(t => checkboxForDefinitionObject(t, onChange)).toArray();

  const defaultDropdowns = renderDefaultDropdowns(useProps);
  const errorMessage = (
    <ModalRow>
      <ModalDescription>
        <div className="warning">{settingsErrorMessages[useProps.validationError]}</div>
      </ModalDescription>
    </ModalRow>
  );

  // The checkbox groups are always in children
  let children = [
    wrapCheckboxGroup(eventCheckboxes, "Event Types"),
    <ModalField key="personLocationTypes">
      <ModalRow>{wrapCheckboxGroup(personCheckboxes, "Person Types")}</ModalRow>
      <ModalRow>{wrapCheckboxGroup(locationCheckboxes, "Location Types")}</ModalRow>
    </ModalField>,
    wrapCheckboxGroup(otherCheckboxes, "Other Types")
  ];
  if (isIos) {
    children = [
      wrapCheckboxGroup(eventCheckboxes, "Event Types"),
      wrapCheckboxGroup(personCheckboxes, "Person Types"),
      wrapCheckboxGroup(locationCheckboxes, "Location Types"),
      wrapCheckboxGroup(otherCheckboxes, "Other Types")
    ];
  }

  // But iOS adds the dropdowns into that group as well, whereas Desktop
  // Needs them in a separate row from the others.
  // So we need to approach each differently.

  let result = null;
  //#!if iOS === true
// children.unshift(defaultDropdowns);
// result = wrapComponents(children, "Item Types");
  //#!else
  result = (
    <ModalGroup
      label="Item Types"
      showButton={true}
      buttonOnClick={() => useProps.onAdvancedSettingsClick(0)}
      buttonLabel="Edit"
    >
      {!useProps.isValid ? errorMessage : null}
      {defaultDropdowns}
      <ModalRow>{children}</ModalRow>
    </ModalGroup>
  );
  //#!endif
  return result;
};

class SettingsTypeGroup extends PureComponent {
  constructor(props, context) {
    super(props, context);
  }

  render() {
    const isIos = preprocessorQuery.isIos();

    return (
      <ModalTabContent scrollable={true}>
        {renderTypes(this.props)}
        {isIos ? (
          <ModalGroup label={isIos ? "Advanced" : null}>
            <ModalRow alignRight={true}>
              <ModalField flex={1}>
                <FormButton
                  linkStyle={!isIos}
                  label="Advanced Settings"
                  onClick={this.props.onAdvancedSettingsClick}
                  rightAlign={true}
                />
              </ModalField>
            </ModalRow>
          </ModalGroup>
        ) : null}
      </ModalTabContent>
    );
  }
}

SettingsTypeGroup.propTypes = {
  types: ImmutablePropTypes.list.isRequired,
  dispatchLocal: PropTypes.func.isRequired,
  onAdvancedSettingsClick: PropTypes.func.isRequired,
  includeNarrative: PropTypes.bool.isRequired,
  orderedSequenceTypes: ImmutablePropTypes.list.isRequired,
  orderedNarrativeTypes: ImmutablePropTypes.list,
  defaultSequenceType: PropTypes.string.isRequired,
  defaultNarrativeType: PropTypes.string,
  isValid: PropTypes.bool,
  validationError: PropTypes.string
};

const mapDispatchToProps = (_dispatch, _props) => {
  // Note: We are always using dispatchLocal here so that the action only travels
  // back to the Settings Modal.
  // Changes are committed to the root store only when the dialog is confirmed.
  return {};
};

export default connect(null, mapDispatchToProps)(SettingsTypeGroup);
